"use client";

import * as React from "react";

/**
 * Brilho sutil que segue o cursor (apenas dispositivos com mouse) e alimenta
 * as variáveis --mx/--my dos cards `.glow-card`. Sem re-render do React:
 * tudo via rAF + CSS variables.
 */
export default function CursorGlow() {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!fine) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const el = ref.current;
    if (!el) return;
    el.style.display = "block"; // só existe em dispositivos com mouse

    let raf = 0;
    let running = false;
    let tx = 0;
    let ty = 0;
    let x = 0;
    let y = 0;
    let shown = false;
    const half = 260;

    const tick = () => {
      const k = reduce ? 1 : 0.14;
      x += (tx - x) * k;
      y += (ty - y) * k;
      el.style.transform = `translate3d(${x - half}px, ${y - half}px, 0)`;
      if (Math.abs(tx - x) > 0.4 || Math.abs(ty - y) > 0.4) {
        raf = requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      tx = e.clientX;
      ty = e.clientY;
      if (!shown) {
        shown = true;
        x = tx;
        y = ty;
        el.style.opacity = "1";
      }
      if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
      const card = (e.target as Element | null)?.closest?.<HTMLElement>(
        ".glow-card"
      );
      if (card) {
        const r = card.getBoundingClientRect();
        card.style.setProperty("--mx", `${e.clientX - r.left}px`);
        card.style.setProperty("--my", `${e.clientY - r.top}px`);
      }
    };
    const onLeave = () => {
      el.style.opacity = "0";
      shown = false;
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed left-0 top-0 z-[5] hidden h-[520px] w-[520px] rounded-full opacity-0 transition-opacity duration-500"
      style={{
        background:
          "radial-gradient(circle, rgb(var(--electric) / 0.08) 0%, rgb(var(--brand) / 0.035) 35%, transparent 65%)",
        willChange: "transform",
      }}
    />
  );
}
